import { useState, useRef, useEffect } from 'react';
import Modal from '@Components/Shared/Modals/Modal';
import translator from '@Components/Utilities/Translator';
import Button from '@mui/material/Button';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import PhoneField from '@Components/MuiWrappers/PhoneField';
import atoms from '@Components/Utilities/SandhillsAtoms';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import TranslationText from '@Components/Utilities/TranslationText';
import { styled } from '@mui/material/styles';
import { useRecoilValue } from 'recoil';
import { sandhillsRequest } from '@Components/Utilities/SandhillsUtility';
import { subscriptionChannelType } from '@Components/Utilities/SandhillsConstants';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import Grid from '@mui/material/Grid';
import Checkbox from '@mui/material/Checkbox';
import { smsType } from '@Components/Utilities/SandhillsConstants';
import { loadable } from '@Components/Utilities/SandhillsLoadable';

const LoadingRing = loadable(() => import('@Components/MuiWrappers/LoadingRing'));

const CheckboxGrid = styled(Grid)({
    display: 'flex',
    alignItems: 'center'
});

const CheckboxField = styled(Checkbox)({
    alignSelf: 'flex-start',
    padding: '0px'
});

const TextSignUpModal = (props) => {

    const intlTelInput = useRef(null);

    const [phone, setPhone] = useState('');
    const [currentSMSPhone, setCurrentSMSPhone] = useState('');
    const [showPhoneConfirmation, setShowPhoneConfirmation] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [message, setMessage] = useState('');
    const [firstname, setFirstname] = useState('');
    const [loading, setLoading] = useState(false);
    const [isChecked, setIsChecked] = useState(false);
    const [isOpen, setIsOpen] = useState(true);
    const routes = useRecoilValue(atoms.routes);
    const currentUser = useRecoilValue(atoms.currentUser)

    const onSubmit = (isSubscribed) => {
        if (isSubscribed) {
            const errors = run({ Phone: phone }, [ruleRunner('Phone', translator.translate('sandhills.phone'), ValidationRules.required)]);
            if (!errors?.Phone && intlTelInput.current?.isValidNumber() == false) {
                errors.Phone = translator.translate('sandhills.invalidphone').toString();
            }

            setValidationErrors(errors);
            if (Object.keys(errors).length <= 0) {
                if (isChecked) {
                    if (intlTelInput.current?.getSelectedCountryData().name.toLowerCase() !== 'united states') {
                        setValidationErrors({ Phone: translator.translate('sandhills.sorrythisisonlyforusphone') });
                    } else if (currentUser.IsAuthenticated) {
                        setLoading(true);
                        const formattedPhone = intlTelInput.current?.getNumber() ?? phone;

                        sandhillsRequest({
                            url: routes.CheckShowSMSPhoneUpdateConfirmation,
                            method: 'post',
                            data: {
                                smsPhone: formattedPhone,
                                smsSignUpTypeID: smsType.marketing
                            }
                        }).then((response) => {
                            if (response.showConfirmation) {
                                setLoading(false);
                                setCurrentSMSPhone(response.oldSMSPhone);
                                setShowPhoneConfirmation(true);
                            } else {
                                onSubscribe(true);
                            }
                        }).catch((error) => {
                            console.log(error);
                            setLoading(false);
                            setValidationErrors({ Phone: translator.translate('sandhills.errortryagain') });
                        });
                    } else {
                        onSubscribe(true);
                    }
                } else {
                    setValidationErrors({ Phone: translator.translate('sandhills.tosignuppleasecheckthebox') });
                }
            }
        } else {
            onSubscribe(false);
        }
    };

    const onSubscribe = (isSubscribed) => {
        if (isSubscribed && firstname === '') {
            setLoading(true);
            sandhillsRequest({
                url: routes.SubscribeUser,
                    method: 'post',
                    data: {
                        user: intlTelInput.current?.getNumber() ?? phone,
                        subscriptionChannelTypeID: subscriptionChannelType.textMessage,
                        subscribe: true,
                        webAgreementTypeIDs: props.WebAgreementTypeIDs,
                        context: "TextSignUpModal",
                        pendingSubscriptionToken: props.pendingSubscriptionToken || undefined
                    },
                errorHandling: {
                    showDetails: true,
                    useConsole: true,
                    useAlert: false
                }
            }).then((response) => {
                setLoading(false);
                if (response) {
                    setValidationErrors({});
                    setMessage(translator.translate('sandhills.tradepubwebsite.mvc.thankyouforyoursubmission'));
                    setTimeout(() => {
                        onClose(isSubscribed)
                    }, 3000);
                } else {
                    setValidationErrors({ Phone: translator.translate('sandhills.errortryagain') });
                }
            }).catch((error) => {
                setLoading(false);
                setValidationErrors({ Phone: translator.translate('sandhills.errortryagain') });
            });
        } else {
            onClose(isSubscribed);
        }
    };

    const onClose = (isSubscribed) => {
        setIsOpen(false);
        setMessage('');
        setPhone('');
        setShowPhoneConfirmation(false);
        setIsChecked(false);
        setValidationErrors({});
        if (props.onSubmit !== undefined && typeof props.onSubmit === 'function') {
            props.onSubmit(isSubscribed, intlTelInput.current?.getNumber() ?? phone);
        }
        props.setShowTsuModal(false);
    };

    const handleChange = (event) => {
        setFirstname(event.target.value);
    };

    const onCheckboxChange = (e) => {
        const checked = e.target.checked;
        setIsChecked(checked);
    };

    return (
        <>{isOpen &&
            <Modal containerClassName='text-signup' contentClassName='text-signup-content' onEscapeKeyClose={onClose}>
                <Modal.Header onClose={() => onSubmit(false)}>
                    <Modal.Title></Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p id='description-label' className='hidden'>Sign up for text alerts from {props.PublicationName}</p>
                    <Stack sx={{ alignItems: 'center', textAlign: 'center' }} spacing={2}>
                        <img src={props.LogoSrc} alt={props.PublicationName} style={{ width: '60%' }} />
                        <Typography variant='h6' component='span'>
                            <strong><TranslationText tKey='sandhills.dontmissout' /></strong>
                        </Typography>
                        <Typography id='tsu-subheader' style={{ fontSize: '16px' }}>
                            <TranslationText tKey='Sandhills.StayUpToDateAndReceiveTextMessages' />
                        </Typography>

                        <CheckboxGrid item xs={12} style={{ maxWidth: '100%', textWrap: 'auto' }}>
                            <CheckboxField
                                size='small'
                                id='TextSignUpCheckbox'
                                checked={isChecked}
                                onChange={onCheckboxChange}
                                disabled={showPhoneConfirmation}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        onCheckboxChange({
                                            target: {
                                                checked: !isChecked
                                            }
                                        });
                                    }
                                }}
                                inputProps={{ 'aria-labelledby': 'SubscriptionPolicy' }}
                            />
                            <Typography style={{ fontSize: '11px' }} id='SubscriptionPolicy' dangerouslySetInnerHTML={{ __html: props.SubscriptionPolicy }} />
                        </CheckboxGrid>
                        <Typography id='tsu-policy' style={{ fontSize: '11px', maxWidth: '100%', textWrap: 'auto' }} dangerouslySetInnerHTML={{ __html: props.TermsAndPolicyAgreement }} />
                        <ValidationErrors errors={validationErrors} />
                        <PhoneField
                            id='TextSignUpPhone'
                            name='TextSignUpPhone'
                            size='small'
                            value={phone}
                            onChange={(e) => {
                                setPhone(e.target.value);
                                setValidationErrors({});
                            }}
                            label={translator.translate('sandhills.phonenumber')}
                            error={validationErrors.Phone ? true : false}
                            helperText={validationErrors.Phone}
                            intlTelInput={input => intlTelInput.current = input}
                            disabled={showPhoneConfirmation}
                            fullWidth
                        />
                        <input autoComplete="off" type="hidden" name="firstname" onChange={handleChange} />
                        {message &&
                            <Typography sx={{ fontSize: '16px', pt: 1, pb: 1 }}>{message}</Typography>}

                        {showPhoneConfirmation ?
                            <>
                                <Grid item xs={12}>
                                    <Typography sx={{ fontSize: '12px' }} color='error.main' dangerouslySetInnerHTML={{ __html: props.SMSPhoneUpdateConfirmationText }} />
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography color='error.main' sx={{ fontSize: '12px' }}>
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.oldinfo' /> : {currentSMSPhone}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12}>
                                    <Typography color='error.main' sx={{ fontSize: '12px' }}>
                                        <TranslationText tKey='sandhills.tradepubwebsite.mvc.newinfo' /> : {intlTelInput.current?.getNumber()}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sx={{ display: 'flex', }}>
                                    <Button sx={{ width: '150px', marginRight: '10px' }} onClick={() => onSubscribe(true)}>
                                        {translator.translate('sandhills.ok')}
                                    </Button>
                                    <Button sx={{ width: '150px', marginLeft: '10px' }} onClick={() => onSubscribe(false)}>
                                        {translator.translate('sandhills.cancel')}
                                    </Button>
                                </Grid>
                            </>
                            :
                            <>
                                <Grid item xs={12} sx={{ display: 'flex', }}>
                                    <Button sx={{ width: '150px', marginRight: '10px' }} onClick={() => onSubmit(true)} disabled={loading}>
                                        {translator.translate('sandhills.signup')}
                                    </Button>
                                    <Button sx={{ width: '150px', marginLeft: '10px' }} onClick={() => onSubmit(false)}>
                                        {translator.translate('sandhills.nothankyou')}
                                    </Button>
                                </Grid></>}
                    </Stack>
                </Modal.Body>
                {loading &&
                    <LoadingRing backdrop />}
            </Modal>
        }
        </>
    );
};
export default TextSignUpModal;